import { EmptyState } from "@/components/EmptyState";
import { InfoIcon, OkIcon, WarningIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

import { analytics } from "../lib/telemetry/analytics.ts";

import type { Alert } from "./alerts.ts";

/**
 * La banda de alertas del Resumen.
 *
 * Dibuja UNA forma, venga de donde venga el hallazgo. Es lo que permite que el
 * día que exista el motor de §7.5 solo haya que añadirle un origen a
 * `deriveAlerts`: aquí no hay un `switch` por tipo de alerta y no debe
 * haberlo.
 *
 * Cada fila enseña la consulta que la respalda y lleva su acción: abrir la
 * pantalla donde se ve, o saltar al chat con la pregunta ya escrita.
 */
export function AlertsBand({
  alerts,
  onAsk,
}: {
  readonly alerts: readonly Alert[];
  onAsk: (question: string) => void;
}) {
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Alertas {alerts.length > 0 && `(${String(alerts.length)})`}
      </h2>

      {alerts.length === 0 ? (
        <div className="rounded-lg border bg-card">
          <EmptyState icon={OkIcon}>
            Nada que mirar: ni huecos, ni solapes, ni lotes esperando.
          </EmptyState>
        </div>
      ) : (
        <ul className="grid gap-px overflow-hidden rounded-lg border bg-border">
          {alerts.map((alert) => {
            // En una constante y no leída de `alert` dentro del manejador: el
            // estrechamiento de una propiedad no sobrevive a la clausura.
            const { evidence } = alert;
            // El tipo de alerta sin el id de lo que señala: `ready-batch`.
            const track = (action: string) => {
              analytics.work.alertAction({
                section: "overview",
                alert: alert.id.split(":")[0] ?? alert.id,
                action,
              });
            };

            return (
              <li
                key={alert.id}
                className="flex flex-wrap items-start gap-x-3 gap-y-2 bg-card px-3 py-2.5"
              >
                {alert.severity === "warning" ? (
                  <WarningIcon className="mt-0.5 size-4 shrink-0 text-warning" />
                ) : (
                  <InfoIcon className="mt-0.5 size-4 shrink-0 text-primary" />
                )}

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{alert.title}</p>
                  {alert.detail !== null && (
                    <p className="text-sm text-muted-foreground">
                      {alert.detail}
                    </p>
                  )}
                  {/* La evidencia se ENSEÑA: es lo que convierte un número en
                    algo que alguien puede ir a comprobar (§7.5). */}
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                    {evidence.query}
                  </p>
                </div>

                {evidence.kind === "open" ? (
                  <Button variant="outline" size="sm" asChild>
                    {evidence.target.screen === "batch" ? (
                      <Link
                        to="/work/lotes/$batchId"
                        params={{ batchId: evidence.target.batchId }}
                        search={(previous) => previous}
                        onClick={() => {
                          track("open_batch");
                          analytics.work.batchOpened({
                            section: "overview",
                            origin: "alert",
                          });
                        }}
                      >
                        {evidence.label}
                      </Link>
                    ) : (
                      <Link
                        to="/memoria/mecanicas/$period/$slug"
                        params={{
                          period: evidence.target.period,
                          slug: evidence.target.slug,
                        }}
                        search={(previous) => previous}
                        onClick={() => {
                          track("open_mechanic");
                        }}
                      >
                        {evidence.label}
                      </Link>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      track("ask");
                      onAsk(evidence.question);
                    }}
                  >
                    {evidence.label}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
